import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { UiPanelComponent } from '@template-ui/components';
import { UiButtonDirective } from '@template-ui/directives';
import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import type { WorkflowOverviewStep } from './workflow-overview-presentation';
import { workflowStepInspectionPresentation } from './workflow-step-inspection-presentation';

/** Read-only selection summary. The workspace owns deliberate editor entry. */
@Component({
  selector: 'phms-workflow-step-inspection',
  imports: [NgTemplateOutlet, UiPanelComponent, UiButtonDirective],
  templateUrl: './workflow-step-inspection.component.html',
  styleUrl: './workflow-step-inspection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowStepInspectionComponent {
  readonly node = input.required<WorkflowDefinitionNode>();
  readonly document = input<WorkflowDefinitionDocument | null>(null);
  readonly overview = input<WorkflowOverviewStep | null>(null);
  readonly blocked = input(false);
  readonly openEditor = output<void>();
  readonly terminalResult = computed(() => {
    const node = this.node(),
      value = node.terminalOutcome;
    const known =
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value.version === 1 &&
      typeof value.code === 'string' &&
      !!value.code.trim() &&
      typeof value.label === 'string' &&
      !!value.label.trim();
    return {
      label: known
        ? value.label
        : value == null
          ? 'No saved result'
          : 'Saved result needs inspection',
      detail: known
        ? 'Related work has separate settings for which saved results it accepts.'
        : value === null
          ? 'The saved workflow result is explicitly null.'
          : value === undefined
            ? 'No workflow result is declared.'
            : 'Open the step editor to inspect the retained result settings.',
      raw: Object.prototype.hasOwnProperty.call(node, 'terminalOutcome')
        ? (JSON.stringify(value, null, 2) ?? 'Not recorded')
        : null,
    };
  });
  readonly presentation = computed(() => {
    const node = this.node();
    const presentation = workflowStepInspectionPresentation(
      this.document(),
      node,
    );
    const names = new Map<string, string>();
    const commands = node.declaration?.behavior?.commands;
    if (Array.isArray(commands)) {
      commands.forEach((command, index) => {
        const name = this.savedActionName(command);
        if (name) names.set(`command:${index}`, name);
      });
    }
    return {
      ...presentation,
      actions: presentation.actions.map((action) => ({
        ...action,
        label:
          names.get(action.key) ??
          (action.label === 'Incomplete saved command'
            ? action.label
            : `Saved action: ${action.label}`),
      })),
    };
  });

  /** Names a known saved command identity without asserting installation or availability. */
  private savedActionName(command: unknown): string | null {
    if (
      !command ||
      typeof command !== 'object' ||
      Array.isArray(command) ||
      !('capability' in command)
    )
      return null;
    const capability = command.capability;
    if (
      !capability ||
      typeof capability !== 'object' ||
      Array.isArray(capability) ||
      !('key' in capability)
    )
      return null;
    const key = capability.key;
    if (
      !key ||
      typeof key !== 'object' ||
      Array.isArray(key) ||
      !('provider' in key) ||
      key.provider !== 'workflow' ||
      !('kind' in key) ||
      key.kind !== 'command' ||
      !('code' in key) ||
      !('version' in key)
    )
      return null;
    switch (key.code) {
      case 'workflow.step.complete':
        return key.version === 1 ? 'Complete step' : null;
      case 'workflow.form.save':
        return key.version === 1 ? 'Save form' : null;
      case 'workflow.form.submit':
        return key.version === 1 ? 'Capture form' : null;
      case 'workflow.preparation.submit':
        return key.version === 1 || key.version === 2
          ? 'Submit prepared work'
          : null;
      case 'workflow.review.request':
        return key.version === 1 || key.version === 2 ? 'Request review' : null;
      case 'workflow.review.submit':
        return key.version === 1 ? 'Submit review' : null;
      case 'workflow.review.record':
        return key.version === 1 || key.version === 2 ? 'Record review' : null;
      default:
        return null;
    }
  }
}
